import './style.css';
import type {
  AccountInfo,
  CarConfig,
  CircuitDescriptor,
  CircuitParams,
  CircuitSummary,
  RoomSummary,
  ServerMessage,
  SurfaceConfigs,
  Track,
} from '@microligue/shared';
import {
  BOT_SKILL_IDS,
  BOT_SKILL_LABELS,
  CIRCUIT_PARAM_BOUNDS,
  MAX_BOTS,
  applyLiveryToCarConfig,
  buildTrackFromDesign,
  clampCircuitParams,
  createTestTrack,
  defaultCarConfig,
  defaultSurfaceConfigs,
  generateCircuit,
  parseClientMessage,
} from '@microligue/shared';
import type { BotSkillId } from '@microligue/shared';
import { CarConfigurator } from './car/ui';
import { defaultCamera } from './game/camera';
import { loadDisplaySettings } from './game/display';
import { TrackEditor } from './game/editor';
import { Hud } from './game/hud';
import { formatLapTime } from './game/hud';
import { KeyboardInput } from './game/input';
import { LeagueScreen } from './game/league-ui';
import { NetGame } from './game/netgame';
import { drawTrackPreview } from './game/preview';
import { RaceUi } from './game/race-ui';
import { renderCircuitStats, renderProfile } from './game/stats-ui';
import { TuningPanel } from './game/tuning';
import { api } from './net/api';
import { Connection, loadNetSim } from './net/connection';

const CAR_PALETTE = [
  '#ff4757',
  '#ffa502',
  '#ffd32a',
  '#2ed573',
  '#17c0eb',
  '#3742fa',
  '#a55eea',
  '#ff6b81',
  '#ffffff',
  '#111827',
];

function mustGet<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Élément introuvable : #${id}`);
  return el as T;
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

const screens = {
  auth: mustGet<HTMLElement>('screen-auth'),
  menu: mustGet<HTMLElement>('screen-menu'),
  carconfig: mustGet<HTMLElement>('screen-carconfig'),
  stats: mustGet<HTMLElement>('screen-stats'),
  leagues: mustGet<HTMLElement>('screen-leagues'),
  lobby: mustGet<HTMLElement>('screen-lobby'),
  atelier: mustGet<HTMLElement>('screen-atelier'),
  game: mustGet<HTMLElement>('screen-game'),
};

function showScreen(name: keyof typeof screens): void {
  for (const [key, el] of Object.entries(screens)) {
    el.hidden = key !== name;
  }
}

// ---------------------------------------------------------------------------
// Application state
// ---------------------------------------------------------------------------

const surfaces: SurfaceConfigs = defaultSurfaceConfigs();
const cameraConfig = defaultCamera();
const netSim = loadNetSim();
/** Réglages d'affichage locaux (mode éco) — partagés panneau ↔ jeu, relus en direct. */
const displaySettings = loadDisplaySettings();

let account: AccountInfo | null = null;
let connection: Connection | null = null;
let game: NetGame | null = null;
let panel: TuningPanel | null = null;
let hud: Hud | null = null;
let input: KeyboardInput | null = null;
let raceUi: RaceUi | null = null;

const overlayDisconnected = mustGet<HTMLElement>('overlay-disconnected');

// ---------------------------------------------------------------------------
// Auth screen
// ---------------------------------------------------------------------------

const authPseudo = mustGet<HTMLInputElement>('auth-pseudo');
const authPassword = mustGet<HTMLInputElement>('auth-password');
const authColorField = mustGet<HTMLElement>('auth-color-field');
const authColorsBox = mustGet<HTMLElement>('auth-colors');
const authStatus = mustGet<HTMLElement>('auth-status');
const authSubmit = mustGet<HTMLButtonElement>('auth-submit');
const tabLogin = mustGet<HTMLButtonElement>('auth-tab-login');
const tabRegister = mustGet<HTMLButtonElement>('auth-tab-register');

let authMode: 'login' | 'register' = 'login';
let selectedColor = localStorage.getItem('microligue.color') ?? (CAR_PALETTE[0] as string);

for (const color of CAR_PALETTE) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.style.backgroundColor = color;
  btn.title = color;
  btn.classList.toggle('active', color === selectedColor);
  btn.addEventListener('click', () => {
    selectedColor = color;
    localStorage.setItem('microligue.color', color);
    for (const child of authColorsBox.children) child.classList.remove('active');
    btn.classList.add('active');
  });
  authColorsBox.appendChild(btn);
}

function setAuthMode(mode: 'login' | 'register'): void {
  authMode = mode;
  tabLogin.classList.toggle('active', mode === 'login');
  tabRegister.classList.toggle('active', mode === 'register');
  authColorField.hidden = mode === 'login';
  authSubmit.textContent = mode === 'login' ? 'Se connecter' : 'Créer le compte';
  authPassword.autocomplete = mode === 'login' ? 'current-password' : 'new-password';
  authStatus.textContent = '';
}
tabLogin.addEventListener('click', () => setAuthMode('login'));
tabRegister.addEventListener('click', () => setAuthMode('register'));

async function submitAuth(): Promise<void> {
  const pseudo = authPseudo.value.trim();
  const password = authPassword.value;
  if (!pseudo || password.length < 6) {
    authStatus.textContent = 'Pseudo requis et mot de passe d’au moins 6 caractères.';
    return;
  }
  authSubmit.disabled = true;
  authStatus.textContent = authMode === 'login' ? 'Connexion…' : 'Création…';
  const res =
    authMode === 'login'
      ? await api.login(pseudo, password)
      : await api.register(pseudo, password, selectedColor);
  authSubmit.disabled = false;
  if (!res.ok) {
    authStatus.textContent = res.error;
    return;
  }
  authPassword.value = '';
  onLoggedIn(res.data.me);
}
authSubmit.addEventListener('click', () => void submitAuth());
authPassword.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') void submitAuth();
});

// ---------------------------------------------------------------------------
// Main menu
// ---------------------------------------------------------------------------

const menuGreeting = mustGet<HTMLElement>('menu-greeting');
const menuAdmin = mustGet<HTMLButtonElement>('menu-admin');

function showMenu(): void {
  if (!account) return;
  menuGreeting.textContent = `Bonjour ${account.pseudo}${account.isAdmin ? ' 🛡' : ''} — prêt à rouler ?`;
  menuAdmin.hidden = !account.isAdmin;
  showScreen('menu');
}

mustGet<HTMLButtonElement>('menu-play').addEventListener('click', () => {
  connection?.send({ type: 'listRooms' });
  showScreen('lobby');
});
mustGet<HTMLButtonElement>('menu-car').addEventListener('click', () => openCarConfig());
mustGet<HTMLButtonElement>('menu-atelier').addEventListener('click', () => {
  connection?.send({ type: 'listCircuits' });
  showScreen('atelier');
});
mustGet<HTMLButtonElement>('menu-leagues').addEventListener('click', () => openLeagues());
mustGet<HTMLButtonElement>('menu-profile').addEventListener('click', () => {
  if (account) void openProfile(account.pseudo);
});
menuAdmin.addEventListener('click', () => void openAdmin());
mustGet<HTMLButtonElement>('menu-logout').addEventListener('click', () => void logout());

async function logout(): Promise<void> {
  await api.logout();
  connection?.disconnect();
  connection = null;
  account = null;
  stopGame();
  authPassword.value = '';
  authStatus.textContent = '';
  setAuthMode('login');
  showScreen('auth');
}

// ---------------------------------------------------------------------------
// « Ma voiture » — bumper-car configurator
// ---------------------------------------------------------------------------

const carConfigurator = new CarConfigurator(mustGet<HTMLElement>('carconfig-body'), {
  onSave: (design) => void saveCarDesign(design),
  onBack: () => showMenu(),
});

function openCarConfig(): void {
  if (!account) return;
  showScreen('carconfig');
  carConfigurator.open(account.design ?? defaultCarConfig(account.color));
}

async function saveCarDesign(design: CarConfig): Promise<void> {
  const res = await api.setDesign(design);
  if (!res.ok) {
    if (handleUnauthorized(res.status)) return;
    carConfigurator.setStatus(res.error);
    return;
  }
  account = res.data.me;
  carConfigurator.setStatus('Voiture enregistrée ✓ — visible dès votre prochaine entrée en salon.');
}

// ---------------------------------------------------------------------------
// Stats screens (profile / circuit / admin)
// ---------------------------------------------------------------------------

const statsBody = mustGet<HTMLElement>('stats-body');
/**
 * The screen the stats screen returns to. Captured ONCE when entering stats
 * from a non-stats screen, so cross-navigation (profile ↔ circuit ↔ admin,
 * all on the stats screen) doesn't trap the back button on 'stats' itself.
 */
let statsReturn: keyof typeof screens = 'menu';

function enterStats(): void {
  if (screens.stats.hidden) {
    statsReturn = !screens.game.hidden
      ? 'game'
      : !screens.atelier.hidden
        ? 'atelier'
        : !screens.lobby.hidden
          ? 'lobby'
          : 'menu';
  }
  statsBody.replaceChildren(document.createTextNode('Chargement…'));
  showScreen('stats');
}

mustGet<HTMLButtonElement>('stats-back').addEventListener('click', () => showScreen(statsReturn));

const statsCb = {
  onOpenCircuit: (id: string) => void openCircuit(id),
  onOpenProfile: (pseudo: string) => void openProfile(pseudo),
};

/** If an API call 401s mid-session, the session is gone → back to the auth screen. */
function handleUnauthorized(status: number): boolean {
  if (status !== 401) return false;
  connection?.disconnect();
  connection = null;
  account = null;
  stopGame();
  showScreen('auth');
  authStatus.textContent = 'Session expirée — reconnectez-vous.';
  return true;
}

let currentProfilePseudo: string | null = null;

async function openProfile(pseudo: string): Promise<void> {
  enterStats();
  currentProfilePseudo = pseudo;
  const res = await api.profile(pseudo);
  if (!res.ok) {
    if (handleUnauthorized(res.status)) return;
    statsBody.replaceChildren(document.createTextNode(res.error));
    return;
  }
  const own = account !== null && pseudo.toLowerCase() === account.pseudo.toLowerCase();
  renderProfile(
    statsBody,
    res.data.profile,
    statsCb,
    own && account
      ? {
          worn: account.livery,
          baseDesign: account.design ?? defaultCarConfig(account.color),
          onEquip: (livery) => connection?.send({ type: 'equipLivery', livery }),
        }
      : undefined,
  );
  // Own profile: shortcut to the bumper-car configurator.
  if (own) {
    const wrap = document.createElement('div');
    wrap.className = 'stats-color-changer';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '🎨 Personnaliser ma voiture';
    btn.addEventListener('click', () => openCarConfig());
    wrap.appendChild(btn);
    statsBody.appendChild(wrap);
  }
}

async function openCircuit(id: string): Promise<void> {
  enterStats();
  const res = await api.circuitStats(id);
  if (!res.ok) {
    if (handleUnauthorized(res.status)) return;
    statsBody.replaceChildren(document.createTextNode(res.error));
    return;
  }
  renderCircuitStats(statsBody, res.data.stats, statsCb);
}

async function openAdmin(): Promise<void> {
  enterStats();
  const res = await api.players();
  if (!res.ok) {
    if (handleUnauthorized(res.status)) return;
    statsBody.replaceChildren(document.createTextNode(res.error));
    return;
  }
  statsBody.replaceChildren();
  const h = document.createElement('h2');
  h.textContent = '🛡 Administration';
  statsBody.appendChild(h);
  const sub = document.createElement('div');
  sub.className = 'stats-sub';
  sub.textContent = 'Promouvoir des joueurs admin · la validation des circuits est dans l’atelier.';
  statsBody.appendChild(sub);

  for (const p of res.data.players) {
    const row = document.createElement('div');
    row.className = 'lobby-room';
    const meta = document.createElement('div');
    meta.className = 'circuit-meta';
    const name = document.createElement('button');
    name.className = 'link-btn';
    name.textContent = p.pseudo + (p.isAdmin ? ' 🛡' : '');
    name.addEventListener('click', () => void openProfile(p.pseudo));
    const stat = document.createElement('span');
    stat.className = 'circuit-record';
    stat.textContent = `${p.races} course(s) · ${p.wins} victoire(s)`;
    meta.append(name, stat);
    row.appendChild(meta);
    if (!p.isAdmin) {
      const promote = document.createElement('button');
      promote.type = 'button';
      promote.textContent = 'Promouvoir admin';
      promote.addEventListener('click', async () => {
        promote.disabled = true;
        const r = await api.promote(p.pseudo);
        if (r.ok) void openAdmin();
        else promote.disabled = false;
      });
      row.appendChild(promote);
    }
    statsBody.appendChild(row);
  }
}

// ---------------------------------------------------------------------------
// Leagues screen
// ---------------------------------------------------------------------------

const leaguesStatus = mustGet<HTMLElement>('leagues-status');

const leagueScreen = new LeagueScreen(mustGet<HTMLElement>('leagues-body'), {
  isAdmin: () => account?.isAdmin ?? false,
  circuits: () => circuits,
  fetchPlayers: async () => {
    const res = await api.players();
    if (!res.ok) {
      handleUnauthorized(res.status);
      return [];
    }
    return res.data.players;
  },
  send: {
    listLeagues: () => connection?.send({ type: 'leagueList' }),
    createLeague: (input) =>
      connection?.send({
        type: 'leagueCreate',
        name: input.name,
        divisions: input.divisions,
        promoteN: input.promoteN,
        relegateN: input.relegateN,
      }),
    getSeason: (seasonId) => connection?.send({ type: 'seasonGet', seasonId }),
    createSeason: (leagueId, circuitIds, assignments) =>
      connection?.send({ type: 'seasonCreate', leagueId, circuitIds, assignments }),
    launchRound: (seasonId, divisionLevel) =>
      connection?.send({ type: 'leagueLaunchRound', seasonId, divisionLevel }),
    closeSeason: (seasonId) => connection?.send({ type: 'seasonClose', seasonId }),
    getArchives: (leagueId) => connection?.send({ type: 'leagueArchives', leagueId }),
    getArchive: (seasonId) => connection?.send({ type: 'seasonArchiveGet', seasonId }),
  },
  onOpenProfile: (pseudo) => void openProfile(pseudo),
  onBack: () => showMenu(),
  status: (msg) => {
    leaguesStatus.textContent = msg;
  },
});

function openLeagues(): void {
  leaguesStatus.textContent = '';
  showScreen('leagues');
  leagueScreen.open();
}

// ---------------------------------------------------------------------------
// Lobby screen
// ---------------------------------------------------------------------------

const roomsBox = mustGet<HTMLElement>('lobby-rooms');
const lobbyStatus = mustGet<HTMLElement>('lobby-status');
const createForm = mustGet<HTMLFormElement>('lobby-create');
const createName = mustGet<HTMLInputElement>('create-name');
const createMax = mustGet<HTMLSelectElement>('create-max');
const createBots = mustGet<HTMLSelectElement>('create-bots');
const createBotSkill = mustGet<HTMLSelectElement>('create-bot-skill');
const lobbyBotsHint = mustGet<HTMLElement>('lobby-bots-hint');
const createCircuit = mustGet<HTMLSelectElement>('create-circuit');
let circuits: CircuitSummary[] = [];

// Race setup (humans / AI opponents / AI level) — persisted per device so a
// test configuration survives reloads between two sessions.
const CREATE_SETUP_KEY = 'microligue.createRoom.v1';
const storedSetup = ((): { humans: number; bots: number; skill: BotSkillId } => {
  const fallback = { humans: 2, bots: 0, skill: 'fast' as BotSkillId };
  try {
    const raw = JSON.parse(localStorage.getItem(CREATE_SETUP_KEY) ?? '');
    const humans = Math.min(Math.max(Math.round(Number(raw.humans)), 1), 8);
    const bots = Math.min(Math.max(Math.round(Number(raw.bots)), 0), MAX_BOTS);
    const skill = (BOT_SKILL_IDS as readonly string[]).includes(String(raw.skill))
      ? (String(raw.skill) as BotSkillId)
      : fallback.skill;
    if (!Number.isFinite(humans) || !Number.isFinite(bots)) return fallback;
    return { humans, bots: Math.min(bots, 8 - humans), skill };
  } catch {
    return fallback;
  }
})();

for (let n = 1; n <= 8; n++) {
  const opt = document.createElement('option');
  opt.value = String(n);
  opt.textContent = `${n} humain${n > 1 ? 's' : ''}`;
  if (n === storedSetup.humans) opt.selected = true;
  createMax.appendChild(opt);
}
for (const id of BOT_SKILL_IDS) {
  const opt = document.createElement('option');
  opt.value = id;
  opt.textContent = `IA : ${BOT_SKILL_LABELS[id]}`;
  if (id === storedSetup.skill) opt.selected = true;
  createBotSkill.appendChild(opt);
}

/** Rebuilds the AI count options so humans + bots never exceed 8 cars. */
function refreshBotOptions(): void {
  const humans = Number(createMax.value);
  const cap = Math.min(MAX_BOTS, 8 - humans);
  const wanted = Math.min(Number(createBots.value || storedSetup.bots), cap);
  createBots.replaceChildren();
  for (let n = 0; n <= cap; n++) {
    const opt = document.createElement('option');
    opt.value = String(n);
    opt.textContent = n === 0 ? 'Sans IA' : `${n} IA`;
    if (n === wanted) opt.selected = true;
    createBots.appendChild(opt);
  }
  const bots = Number(createBots.value);
  createBotSkill.hidden = bots === 0;
  lobbyBotsHint.hidden = bots === 0;
}
refreshBotOptions();

function persistCreateSetup(): void {
  localStorage.setItem(
    CREATE_SETUP_KEY,
    JSON.stringify({
      humans: Number(createMax.value),
      bots: Number(createBots.value),
      skill: createBotSkill.value,
    }),
  );
}
createMax.addEventListener('change', () => {
  refreshBotOptions();
  persistCreateSetup();
});
createBots.addEventListener('change', () => {
  refreshBotOptions();
  persistCreateSetup();
});
createBotSkill.addEventListener('change', persistCreateSetup);

createForm.addEventListener('submit', (e) => {
  e.preventDefault();
  const name = createName.value.trim();
  if (!name || !connection) return;
  if (!createCircuit.value) {
    lobbyStatus.textContent = 'Aucun circuit validé disponible.';
    return;
  }
  const humans = Number(createMax.value);
  const bots = Number(createBots.value);
  connection.send({
    type: 'createRoom',
    name,
    // Total capacity = human seats + AI opponents (the server seats the bots).
    maxPlayers: Math.min(8, humans + bots),
    circuitId: createCircuit.value,
    ...(bots > 0 ? { bots, botSkill: createBotSkill.value as BotSkillId } : {}),
  });
});

mustGet<HTMLButtonElement>('lobby-solo').addEventListener('click', () => {
  connection?.send({
    type: 'createRoom',
    name: `Solo de ${account?.pseudo ?? 'Pilote'}`.slice(0, 30),
    maxPlayers: 1,
    circuitId: createCircuit.value || 'test',
  });
});

mustGet<HTMLButtonElement>('lobby-atelier').addEventListener('click', () => {
  connection?.send({ type: 'listCircuits' });
  showScreen('atelier');
});
mustGet<HTMLButtonElement>('lobby-back').addEventListener('click', () => showMenu());

function refreshCircuitSelect(): void {
  const previous = createCircuit.value;
  createCircuit.replaceChildren();
  // Validated circuits first, then drafts (playable in ordinary rooms so a
  // freshly drawn circuit is testable at once — leagues remain validated-only).
  const sorted = [...circuits].sort((a, b) =>
    a.status === b.status ? 0 : a.status === 'validated' ? -1 : 1,
  );
  for (const c of sorted) {
    const opt = document.createElement('option');
    opt.value = c.id;
    opt.textContent = c.status === 'validated' ? c.name : `${c.name} (brouillon)`;
    createCircuit.appendChild(opt);
  }
  if ([...createCircuit.options].some((o) => o.value === previous)) {
    createCircuit.value = previous;
  }
}

function renderRooms(rooms: RoomSummary[]): void {
  roomsBox.replaceChildren();
  if (rooms.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'lobby-empty';
    empty.textContent = 'Aucun salon ouvert — créez-en un !';
    roomsBox.appendChild(empty);
    return;
  }
  for (const room of rooms) {
    const row = document.createElement('div');
    row.className = 'lobby-room';
    const racing = room.state !== 'waiting';
    const label = document.createElement('span');
    label.textContent =
      `${room.name} · ${room.circuitName} — ${room.players}/${room.maxPlayers}` +
      (room.bots ? ` (dont ${room.bots} IA)` : '') +
      (racing ? ` · 🏁 course en cours (${room.laps} tours)` : '');
    if (room.league) {
      const badge = document.createElement('span');
      badge.className = 'badge badge-league';
      badge.textContent = `🏆 Ligue · D${room.league.divisionLevel + 1} · manche ${room.league.roundIndex + 1}`;
      badge.title = 'Manche de ligue — réservée aux pilotes de la division.';
      label.append(' ', badge);
    }
    const join = document.createElement('button');
    join.type = 'button';
    join.textContent = racing ? 'En course' : 'Rejoindre';
    join.disabled = racing || room.players >= room.maxPlayers;
    join.addEventListener('click', () => connection?.send({ type: 'joinRoom', roomId: room.id }));
    row.append(label, join);
    roomsBox.appendChild(row);
  }
}

// ---------------------------------------------------------------------------
// Atelier
// ---------------------------------------------------------------------------

const atelierCard = mustGet<HTMLElement>('screen-atelier').querySelector(
  '.atelier-card',
) as HTMLElement;
const atTabGen = mustGet<HTMLButtonElement>('at-tab-gen');
const atTabDraw = mustGet<HTMLButtonElement>('at-tab-draw');
const atelierGenerator = mustGet<HTMLElement>('atelier-generator');
const atelierEditor = mustGet<HTMLElement>('atelier-editor');
let editor: TrackEditor | null = null;

function showAtelierTab(tab: 'gen' | 'draw'): void {
  atTabGen.classList.toggle('active', tab === 'gen');
  atTabDraw.classList.toggle('active', tab === 'draw');
  atelierGenerator.hidden = tab !== 'gen';
  atelierEditor.hidden = tab !== 'draw';
  atelierCard.classList.toggle('editor-open', tab === 'draw');
  if (tab === 'draw') {
    if (!editor) {
      editor = new TrackEditor(atelierEditor, {
        onTry: (design) => {
          const payload = { type: 'tryCircuit' as const, design };
          // Pre-flight through the REAL network schema: a payload the server
          // would silently drop must never leave without an explanation.
          if (!parseClientMessage(JSON.stringify(payload))) {
            editor?.showMessage(
              'Tracé hors limites — rapprochez le circuit du centre de la zone et raccourcissez les poignées extrêmes.',
            );
            return;
          }
          connection?.send(payload);
        },
        onSave: (design, name) => {
          const payload = { type: 'saveCircuit' as const, name, design };
          if (!parseClientMessage(JSON.stringify(payload))) {
            editor?.setSaveBusy(false);
            editor?.showMessage(
              'Tracé hors limites — rapprochez le circuit du centre de la zone et raccourcissez les poignées extrêmes.',
            );
            return;
          }
          pendingSave = 'editor';
          connection?.send(payload);
          atelierStatus.textContent = 'Enregistrement…';
          editor?.showMessage('Enregistrement…');
          // Watchdog: never leave « Enregistrement… » hanging silently.
          if (saveWatchdog !== null) window.clearTimeout(saveWatchdog);
          saveWatchdog = window.setTimeout(() => {
            saveWatchdog = null;
            if (pendingSave === 'editor') {
              pendingSave = null;
              editor?.setSaveBusy(false);
              const msg = 'Pas de réponse du serveur — vérifiez la connexion et réessayez.';
              editor?.showMessage(msg);
              atelierStatus.textContent = msg;
            }
          }, 6000);
        },
        status: (msg) => {
          atelierStatus.textContent = msg;
          editor?.showMessage(msg);
        },
      });
    }
    editor.open();
  }
}
atTabGen.addEventListener('click', () => showAtelierTab('gen'));
atTabDraw.addEventListener('click', () => showAtelierTab('draw'));

const atCorners = mustGet<HTMLInputElement>('at-corners');
const atMinW = mustGet<HTMLInputElement>('at-minw');
const atMaxW = mustGet<HTMLInputElement>('at-maxw');
const atLength = mustGet<HTMLInputElement>('at-length');
const atSeed = mustGet<HTMLInputElement>('at-seed');
const atStats = mustGet<HTMLElement>('at-stats');
const atTry = mustGet<HTMLButtonElement>('at-try');
const atSave = mustGet<HTMLButtonElement>('at-save');
const atName = mustGet<HTMLInputElement>('at-name');
const atelierStatus = mustGet<HTMLElement>('atelier-status');
const atelierCircuits = mustGet<HTMLElement>('atelier-circuits');
const previewCanvas = mustGet<HTMLCanvasElement>('atelier-preview');

let draftParams: CircuitParams | null = null;
/** Which atelier tab has a save in flight (drives the confirmation message). */
let pendingSave: 'gen' | 'editor' | null = null;
/** Editor save timeout — resets the button if the server never answers. */
let saveWatchdog: number | null = null;

function readAtelierParams(): CircuitParams {
  return clampCircuitParams({
    corners: Number(atCorners.value),
    minWidth: Number(atMinW.value),
    maxWidth: Number(atMaxW.value),
    approxLength: Number(atLength.value),
    seed: Number(atSeed.value),
  });
}

function regeneratePreview(): void {
  const params = readAtelierParams();
  atSeed.value = String(params.seed);
  const generated = generateCircuit(params);
  if (!generated) {
    draftParams = null;
    atTry.disabled = true;
    atSave.disabled = true;
    atStats.textContent = 'Aucun circuit valide avec ces paramètres — essayez une autre graine.';
    previewCanvas.getContext('2d')?.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    return;
  }
  draftParams = params;
  atTry.disabled = false;
  atSave.disabled = false;
  drawTrackPreview(previewCanvas, generated.track);
  atStats.textContent = `Longueur : ${Math.round(generated.track.length)} px · Généré en ${generated.attempts + 1} tentative(s).`;
}

mustGet<HTMLButtonElement>('at-generate').addEventListener('click', regeneratePreview);
mustGet<HTMLButtonElement>('at-dice').addEventListener('click', () => {
  atSeed.value = String(Math.floor(Math.random() * (CIRCUIT_PARAM_BOUNDS.seed.max + 1)));
  regeneratePreview();
});
atTry.addEventListener('click', () => {
  if (draftParams) connection?.send({ type: 'tryCircuit', params: draftParams });
});
atSave.addEventListener('click', () => {
  const name = atName.value.trim();
  if (!name) {
    atelierStatus.textContent = 'Donnez un nom au circuit avant de l’enregistrer.';
    atName.focus();
    return;
  }
  if (draftParams) {
    pendingSave = 'gen';
    atSave.disabled = true;
    connection?.send({ type: 'saveCircuit', name, params: draftParams });
    atelierStatus.textContent = 'Enregistrement…';
  }
});
mustGet<HTMLButtonElement>('atelier-back').addEventListener('click', () => showMenu());

function renderAtelierCircuits(): void {
  atelierCircuits.replaceChildren();
  if (circuits.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'lobby-empty';
    empty.textContent = 'Aucun circuit — générez et enregistrez le premier !';
    atelierCircuits.appendChild(empty);
    return;
  }
  for (const c of circuits) {
    const row = document.createElement('div');
    row.className = 'lobby-room';

    const meta = document.createElement('div');
    meta.className = 'circuit-meta';
    const title = document.createElement('button');
    title.className = 'link-btn';
    title.textContent = `${c.name} · ${Math.round(c.lengthPx / 100) / 10} km fictifs`;
    title.addEventListener('click', () => void openCircuit(c.id));
    const record = document.createElement('span');
    record.className = 'circuit-record';
    record.textContent = c.recordMs
      ? `Record : ${formatLapTime(c.recordMs)} (${c.recordBy ?? '?'})`
      : 'Aucun record';
    meta.append(title, record);

    const status = document.createElement('span');
    status.className = `circuit-status${c.status === 'validated' ? ' validated' : ''}`;
    status.textContent = c.status === 'validated' ? 'validé' : 'brouillon';

    const actions = document.createElement('div');
    actions.className = 'room-actions';
    const drive = document.createElement('button');
    drive.type = 'button';
    drive.textContent = 'Rouler';
    drive.addEventListener('click', () => {
      connection?.send({
        type: 'createRoom',
        name: `${account?.pseudo ?? 'Pilote'} sur ${c.name}`.slice(0, 30),
        maxPlayers: 1,
        circuitId: c.id,
      });
    });
    actions.appendChild(drive);
    // Circuit validation is admin-only (SPEC §8).
    if (c.status === 'draft' && account?.isAdmin) {
      const validate = document.createElement('button');
      validate.type = 'button';
      validate.textContent = 'Valider';
      validate.title = 'Rend le circuit sélectionnable pour les salons et les ligues';
      validate.addEventListener('click', () =>
        connection?.send({ type: 'validateCircuit', circuitId: c.id }),
      );
      actions.appendChild(validate);
    }

    row.append(meta, status, actions);
    atelierCircuits.appendChild(row);
  }
}

/** Rebuilds the room's track locally — every build path is deterministic. */
function buildTrackFor(circuit: CircuitDescriptor): Track | null {
  if (circuit.builtin) return createTestTrack();
  if (circuit.design) {
    try {
      return buildTrackFromDesign(circuit.design);
    } catch {
      return null;
    }
  }
  if (!circuit.params) return null;
  return generateCircuit(circuit.params)?.track ?? null;
}

mustGet<HTMLButtonElement>('game-leave').addEventListener('click', () => {
  connection?.send({ type: 'leaveRoom' });
});

// ---------------------------------------------------------------------------
// Connection + session lifecycle
// ---------------------------------------------------------------------------

function onLoggedIn(me: AccountInfo): void {
  account = me;
  ensureConnection();
  showMenu();
}

function ensureConnection(): void {
  if (connection) return;
  const conn = new Connection(netSim);
  connection = conn;

  if (!input) input = new KeyboardInput(window);
  if (!hud) hud = new Hud(document);
  if (!raceUi) {
    raceUi = new RaceUi(document, {
      onSetLaps: (laps) => connection?.send({ type: 'setRaceLaps', laps }),
      onStart: () => connection?.send({ type: 'startRace' }),
      onBackToLobby: () => connection?.send({ type: 'backToLobby' }),
      onLeaveRoom: () => connection?.send({ type: 'leaveRoom' }),
    });
  }
  if (!panel) {
    panel = new TuningPanel(
      mustGet('tuning-panel'),
      mustGet('tuning-toggle'),
      surfaces,
      cameraConfig,
      netSim,
      displaySettings,
      {
        onPhysicsChanged: (allSurfaces) =>
          connection?.send({ type: 'tuningSet', surfaces: allSurfaces }),
        onSurfaceOverride: (surface) =>
          connection?.send({ type: 'tuningSurfaceOverride', surface }),
        onNetSimChanged: () => connection?.saveSim(),
      },
    );
  }

  conn.onStatus = (connected) => {
    if (connected) {
      overlayDisconnected.hidden = true;
      return;
    }
    // Only show the "connexion perdue" overlay while actually in a game.
    overlayDisconnected.hidden = !(conn.everConnected && !screens.game.hidden);
  };

  conn.onEvicted = () => {
    // A newer tab/device took over this account. Don't fight it — stand down.
    connection = null;
    stopGame();
    overlayDisconnected.hidden = true;
    showScreen('auth');
    authStatus.textContent = 'Session reprise sur un autre onglet ou appareil.';
  };

  conn.onMessage = (msg: ServerMessage) => handleMessage(conn, msg);
  conn.connect();
}

function handleMessage(conn: Connection, msg: ServerMessage): void {
  switch (msg.type) {
    case 'welcome':
      account = msg.account;
      panel?.setCanAdmin(msg.canAdmin);
      panel?.applyServerSurfaces(msg.surfaces);
      conn.send({ type: 'listCircuits' });
      break;
    case 'circuits':
      circuits = msg.circuits;
      refreshCircuitSelect();
      renderAtelierCircuits();
      if (saveWatchdog !== null) {
        window.clearTimeout(saveWatchdog);
        saveWatchdog = null;
      }
      if (pendingSave) {
        if (pendingSave === 'gen') atName.value = '';
        else editor?.notifySaved();
        pendingSave = null;
        atelierStatus.textContent =
          'Circuit enregistré ✓ (brouillon — un admin doit le valider pour les salons).';
      }
      atSave.disabled = !draftParams;
      break;
    case 'tuning':
      panel?.applyServerSurfaces(msg.surfaces);
      break;
    case 'rooms':
      // The server only pushes the room list to clients NOT in a room: getting
      // it while a game is on screen means our reconnection did not reclaim a
      // car (room gone / server restarted). Leave the ghost session.
      if (game) {
        stopGame();
        showScreen('lobby');
      }
      renderRooms(msg.rooms);
      break;
    case 'joined':
      lobbyStatus.textContent = '';
      startGame(conn, msg);
      break;
    case 'left':
      stopGame();
      showScreen('lobby');
      break;
    case 'leagues':
      // Pass visibility: a background broadcast must not re-render or re-fetch
      // (a season re-fetch could otherwise switch the screen mid-race).
      leagueScreen.setLeagues(msg.leagues, !screens.leagues.hidden);
      break;
    case 'season':
      // League reads are only ever initiated from the leagues screen; applying
      // them elsewhere (e.g. while racing) would eject the player. Ignore if
      // the leagues screen isn't the one on view.
      if (!screens.leagues.hidden) {
        leaguesStatus.textContent = '';
        leagueScreen.setSeason(msg.view);
      }
      break;
    case 'archives':
      if (!screens.leagues.hidden) leagueScreen.setArchives(msg.leagueId, msg.seasons);
      break;
    case 'seasonArchive':
      if (!screens.leagues.hidden) {
        leaguesStatus.textContent = '';
        leagueScreen.setArchive(msg.archive);
      }
      break;
    case 'livery':
      if (account) account.livery = msg.livery;
      // Reflect the change immediately if the own profile is on screen.
      if (!screens.stats.hidden && currentProfilePseudo && account) {
        if (currentProfilePseudo.toLowerCase() === account.pseudo.toLowerCase()) {
          void openProfile(account.pseudo);
        }
      }
      break;
    case 'error':
      if (msg.code === 'admin') {
        if (!screens.atelier.hidden) atelierStatus.textContent = msg.message;
        else panel?.notifyAdminRejected();
      } else if (msg.code === 'protocol') {
        conn.disconnect();
        connection = null;
        stopGame();
        overlayDisconnected.hidden = true;
        showScreen('auth');
        authStatus.textContent = msg.message;
      } else if (!screens.leagues.hidden) {
        leaguesStatus.textContent = msg.message;
      } else if (!screens.atelier.hidden) {
        atelierStatus.textContent = msg.message;
        pendingSave = null;
        if (saveWatchdog !== null) {
          window.clearTimeout(saveWatchdog);
          saveWatchdog = null;
        }
        atSave.disabled = !draftParams;
        editor?.setSaveBusy(false);
        editor?.showMessage(msg.message);
      } else if (!screens.lobby.hidden) {
        lobbyStatus.textContent = msg.message;
      }
      break;
    default:
      game?.handleMessage(msg);
      break;
  }
}

function startGame(conn: Connection, msg: Extract<ServerMessage, { type: 'joined' }>): void {
  stopGame();
  if (!input || !hud || !panel || !raceUi) return;
  const track = buildTrackFor(msg.circuit);
  if (!track) {
    lobbyStatus.textContent = 'Impossible de reconstruire ce circuit — rechargez la page.';
    conn.send({ type: 'leaveRoom' });
    showScreen('lobby');
    return;
  }
  const self = msg.players.find((p) => p.id === msg.selfId);
  game = new NetGame(
    mustGet<HTMLCanvasElement>('game'),
    mustGet<HTMLCanvasElement>('minimap'),
    track,
    msg.circuit,
    surfaces,
    cameraConfig,
    conn,
    input,
    hud,
    panel,
    raceUi,
    msg.selfId,
    msg.players,
    msg.cars,
    msg.tick,
    msg.personalBestMs,
    msg.ownLap,
    msg.race,
    {
      pseudo: self?.pseudo ?? account?.pseudo ?? 'Vous',
      // The server sends the merged look in `players`; the fallback rebuilds
      // it locally the same way (should never trigger — self is in the room).
      design:
        self?.design ??
        applyLiveryToCarConfig(
          account?.design ?? defaultCarConfig(account?.color ?? '#ff4757'),
          account?.livery ?? null,
        ),
    },
    msg.surfaceOverride,
    displaySettings,
  );
  showScreen('game');
  game.resize();
  game.start();
}

function stopGame(): void {
  if (game) {
    game.stop();
    game = null;
  }
  // The whole-circuit flip only applies inside a room: leave no stale state.
  panel?.setSurfaceOverrideAvailable(false);
  panel?.setSurfaceOverride(null);
  raceUi?.hide();
  hud?.setRaceInfo(null);
}

window.addEventListener('resize', () => game?.resize());

// ---------------------------------------------------------------------------
// Boot: resume an existing session, or show the auth screen.
// ---------------------------------------------------------------------------

async function boot(): Promise<void> {
  const res = await api.me();
  if (res.ok) {
    onLoggedIn(res.data.me);
  } else {
    setAuthMode('login');
    showScreen('auth');
  }
}
void boot();
